import { Api } from 'laravel-request';
import { isMockModeEnabled } from '#app/mocks/config';
import { resolveMockRoute } from '#app/mocks/router';

type SuccessCallback = (response: any) => void;
type ErrorCallback = (error: unknown) => void;

class MockBuilder {
  private whereClauses: Array<{ field: string; value: unknown }> = [];

  private orderByClauses: Array<{ field: string; direction?: string }> = [];

  constructor(
    private readonly entity: string,
    private readonly method: string,
    private readonly payload?: unknown
  ) {}

  where(field: string, value: unknown): this {
    this.whereClauses.push({ field, value });
    return this;
  }

  orderBy(field: string, direction?: string): this {
    this.orderByClauses.push({ field, direction });
    return this;
  }

  withoutNotify(): this {
    return this;
  }

  private callResolver(page?: number, perPage?: number): unknown {
    return resolveMockRoute({
      entity: this.entity,
      method: this.method,
      payload: this.payload,
      whereClauses: this.whereClauses,
      orderByClauses: this.orderByClauses,
      page,
      perPage,
    });
  }

  private invoke(
    success?: SuccessCallback,
    error?: ErrorCallback,
    page?: number,
    perPage?: number
  ) {
    try {
      const data = this.callResolver(page, perPage);
      success?.({
        result: 'success',
        data,
        meta: [],
      });
    } catch (e) {
      error?.(e);
    }
  }

  call(success?: SuccessCallback, error?: ErrorCallback) {
    this.invoke(success, error);
    return this;
  }

  all(success?: SuccessCallback, error?: ErrorCallback) {
    this.invoke(success, error);
    return this;
  }

  first(success?: SuccessCallback, error?: ErrorCallback) {
    this.invoke(success, error);
    return this;
  }

  paginate(page: number, perPage: number, success?: SuccessCallback, error?: ErrorCallback) {
    this.invoke(success, error, page, perPage);
    return this;
  }
}

const createFactoryWrapper =
  (apiMethodName: string) =>
  (...factoryArgs: unknown[]): any => {
    const apiMethod = (Api as any)[apiMethodName];
    if (typeof apiMethod !== 'function') return undefined;

    const realBuilder = apiMethod.call(Api, ...factoryArgs);

    // laravel-request methods normally receive: (entity, method, payload?)
    const [entity, method, payload] = factoryArgs;
    if (typeof entity !== 'string' || typeof method !== 'string') return realBuilder;

    if (!isMockModeEnabled()) return realBuilder;

    const hasMock = resolveMockRoute({
      entity,
      method,
      payload,
      whereClauses: [],
      orderByClauses: [],
    });

    if (hasMock == null) return realBuilder;

    return new MockBuilder(entity, method, payload);
  };

export const MockableApi = new Proxy(Api as any, {
  get(target, property: string | symbol) {
    // Proxy `get` trap handles JS property access (e.g. Api.get/Api.post), not HTTP GET.
    if (typeof property !== 'string') {
      return Reflect.get(target, property, target);
    }

    // For non-function properties, return original value from Api unchanged.
    const value = Reflect.get(target, property, target);
    if (typeof value !== 'function') return value;

    return createFactoryWrapper(property);
  },
});
